import { GeneralCsvReportEntity } from "domain/entity/GeneralCsvReport/GeneralCsvReportEntity";
import _ from "lodash";
import { monthDropdownOption } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { GeneralCsvReportConstant } from "presentation/constant/GeneralCsvReport/GeneralCsvReportConstant";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useGeneralCsvReportVM } from "presentation/hook/GeneralCsvReport/useGeneralCsvReportVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useGeneralCsvReportTracked } from "presentation/store/GeneralCsvReport/GeneralCsvReportProvider";
import { processReportMissingFields, reportCheckFieldMandatory } from "presentation/utils/report/ReportCheckFieldUtils";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import NbisDateTimePickerComponent from "presentation/view/components/NbisDateTimePickerComponent";
import { OverflowMenuButton } from "presentation/view/components/OverflowMenuButton";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { FieldType, HPHButton, HPHCheckbox, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { Sidebarheader, StyledAction, StyledSidebar } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const GeneralCsvReportPanel = () => {
    const GENERAL_CSV_REPORT_CONSTANT = GeneralCsvReportConstant.Table;
    const [isLoading, setIsLoading] = useState(false);
    const [generalCsvReportState] = useGeneralCsvReportTracked();
    const generalCsvReportVM = useGeneralCsvReportVM();
    const { currentReport, reportIdTypeMap } = generalCsvReportState;
    const messageBarVM = useMessageBarVM();
    const [allFormState, setAllFormState] = useState<{ [key: string]: string }>({});
    const [isInitPanelData, setIsInitPanelData] = useState(true)
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit, allowRead } = anaInfoState;

    const handleReset = useCallback(() => {
        generalCsvReportVM.onResetClick();
    }, [generalCsvReportVM]);

    const onReportTypeChange = useCallback((fieldKey: string, fieldValue: IFieldValue) => {
        setAllFormState({});

        generalCsvReportVM.onReportTypeChanged(fieldKey, fieldValue);
    }, [generalCsvReportVM]);

    useEffect(() => {
        if (isInitPanelData) {
            setIsInitPanelData(false);
            onReportTypeChange("reportType", currentReport.reportType)
        }
    }, [currentReport.reportType, isInitPanelData, onReportTypeChange])

    useEffect(() => {
        if (currentReport.reportType) {
            const newFormState = processReportMissingFields(currentReport)

            setAllFormState(prev => ({
                ...prev,
                ...newFormState
            }));
        }
    }, [currentReport])

    const checkFieldMandatory = useCallback((currentReport: GeneralCsvReportEntity) => {
        let ret = reportCheckFieldMandatory(currentReport, operatingUnit);

        if (!currentReport.reportType) {
            ret = { ...ret, reportType: "Missing", /*mandatoryCheckFail: "Please input the missing value."*/ };
        }
        if (!_.isEmpty(ret)) {
            setAllFormState(ret);
            messageBarVM.showError(ret["mandatoryCheckFail"] || ret["validateFailed"]);
            return true;
        }
        return false;
    }, [messageBarVM, operatingUnit]);

    /*const isValidMonthYear = useCallback((month: string, year: string) => {
        const monthNum = parseInt(month, 10);
        const monthValid = !isNaN(monthNum) && monthNum >= 1 && monthNum <= 12 && month.length === 2;
        const yearNum = parseInt(year, 10);
        const yearValid = !isNaN(yearNum) && yearNum >= 1900 && yearNum <= 2999 && year.length === 4;
        if (!monthValid || !yearValid) {
            return false;
        }
        return true;

    }, []);*/

    const handleExportClick = useCallback((exportType: string) => {
        if (checkFieldMandatory(currentReport)) return;
        /*if (!isValidMonthYear(currentReport.month, currentReport.year)) {
            messageBarVM.showError("Invalid month or year.");
            return;
        }*/
        setIsLoading(true);
        generalCsvReportVM.onExportClick(exportType, reportIdTypeMap, currentReport).then(data => {
            if (data && data.success) {
                setIsLoading(false);
                messageBarVM.showSuccess("Export successfully.");
            } else {
                setIsLoading(false);
                messageBarVM.showError(data.data ?? "Export failure.");
            }
        });
    }, [checkFieldMandatory, currentReport, generalCsvReportVM, reportIdTypeMap, messageBarVM]);

    const handleDownloadClick = useCallback((exportType: string) => {
        if (checkFieldMandatory(currentReport)) return;
        /*if (!isValidMonthYear(currentReport.month, currentReport.year)) {
            messageBarVM.showError("Invalid month or year.");
            return;
        }*/
        setIsLoading(true);
        generalCsvReportVM.onDownloadClick(reportIdTypeMap, currentReport, exportType).then(data => {
            if (data === "success") {
                messageBarVM.showSuccess("Download successfully.");
                setIsLoading(false);
            } else {
                messageBarVM.showError(data);
                setIsLoading(false);
            }
        });
    }, [checkFieldMandatory, currentReport, generalCsvReportVM, reportIdTypeMap, messageBarVM]);

    const memoExportMenu = useMemo(() => {
        return [
            // {
            //     title: GeneralCsvReportConstant.Title.PDF,
            //     disabled: false,
            //     command: () => {
            //         handleExportClick(GeneralCsvReportConstant.Title.PDF);
            //     }
            // },
            {
                title: GeneralCsvReportConstant.Title.CSV,
                disabled: false,
                command: () => {
                    handleExportClick(GeneralCsvReportConstant.Title.CSV);
                }
            },
            // {
            //     title: GeneralCsvReportConstant.Title.RTF,
            //     disabled: false,
            //     command: () => {
            //         handleExportClick(GeneralCsvReportConstant.Title.RTF);
            //     }
            // }
        ]
    }, [handleExportClick])

    const memoDownloadMenu = useMemo(() => {
        return [
            {
                title: GeneralCsvReportConstant.Title.CSV,
                disabled: false,
                command: () => {
                    handleDownloadClick(GeneralCsvReportConstant.Title.CSV);
                }
            },
            // {
            //     title: GeneralCsvReportConstant.Title.RTF,
            //     disabled: false,
            //     command: () => {
            //         handleDownloadClick(GeneralCsvReportConstant.Title.RTF);
            //     }
            // }
        ]
    }, [handleDownloadClick])

    const memoReportType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "490px", marginBottom: "20px", marginTop: "10px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["reportType"] : ""}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport.reportType ?? ''}
                fieldValue={currentReport.reportType}
                fieldLabel={GENERAL_CSV_REPORT_CONSTANT.REPORT_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'reportType'}
                maxLength={60}
                options={generalCsvReportState.dynamicOptions.reportTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue) =>
                    onReportTypeChange(fieldKey, fieldValue && fieldValue.toString())} />
        </div>
        , [allFormState, currentReport.reportType, GENERAL_CSV_REPORT_CONSTANT.REPORT_TYPE, generalCsvReportState.dynamicOptions.reportTypeDropdownOptions, onReportTypeChange])


    const memoFileLocation = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "490px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["fileLocation"] : ""}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport.fileLocation ?? ''}
                fieldValue={currentReport.fileLocation}
                fieldLabel={GENERAL_CSV_REPORT_CONSTANT.FILE_LOCATION}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'fileLocation'}
                maxLength={60}
                options={generalCsvReportState.dynamicOptions.fileLocationDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalCsvReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentReport.fileLocation, GENERAL_CSV_REPORT_CONSTANT.FILE_LOCATION, generalCsvReportState.dynamicOptions.fileLocationDropdownOptions, generalCsvReportVM])

    const memoOpsLine = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "490px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["opsLine"] : ""}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport?.opsLine ?? ''}
                fieldValue={currentReport?.opsLine}
                fieldLabel={GENERAL_CSV_REPORT_CONSTANT.OPS_LINE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'opsLine'}
                maxLength={60}
                isLimitOptions={true}
                options={generalCsvReportState.dynamicOptions.opsLineDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalCsvReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [allFormState, currentReport?.opsLine, GENERAL_CSV_REPORT_CONSTANT.OPS_LINE, generalCsvReportState.dynamicOptions.opsLineDropdownOptions, generalCsvReportVM])

    const memoMonth = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "230px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["month"] : ""}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport?.month ?? ''}
                fieldValue={currentReport?.month ?? ""}
                fieldLabel={GENERAL_CSV_REPORT_CONSTANT.MONTH}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'month'}
                maxLength={2}
                options={monthDropdownOption}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalCsvReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)}
            />
        </div>
        , [allFormState, currentReport?.month, GENERAL_CSV_REPORT_CONSTANT.MONTH, generalCsvReportVM])

    const memoYear = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "230px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessage={allFormState ? allFormState["year"] : ""}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={currentReport?.year ?? ''}
                fieldValue={currentReport?.year ?? ""}
                fieldLabel={GENERAL_CSV_REPORT_CONSTANT.YEAR}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'year'}
                maxLength={4}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalCsvReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)}
            />
        </div>
        , [GENERAL_CSV_REPORT_CONSTANT.YEAR, allFormState, currentReport?.year, generalCsvReportVM])

    const memoDocDateFrom = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "230px", marginBottom: "20px" }}>
            <NbisDateTimePickerComponent
                disabled={false}
                optional={false}
                label={GENERAL_CSV_REPORT_CONSTANT.DOC_DATE_FROM}
                width="230px"
                date={currentReport?.docDateFrom}
                fieldName="docDateFrom"
                errorMessage={allFormState ? allFormState["docDateFrom"] : ""}
                onDateChange={generalCsvReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_CSV_REPORT_CONSTANT.DOC_DATE_FROM, allFormState, currentReport?.docDateFrom, generalCsvReportVM.onHeaderFieldChange])

    const memoDocDateTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "230px", marginBottom: "20px" }}>
            <NbisDateTimePickerComponent
                disabled={false}
                optional={false}
                label={GENERAL_CSV_REPORT_CONSTANT.DOC_DATE_TO}
                width="230px"
                date={currentReport?.docDateTo}
                fieldName="docDateTo"
                errorMessage={allFormState ? allFormState["docDateTo"] : ""}
                onDateChange={generalCsvReportVM.onHeaderFieldChange} />
        </div>
        , [GENERAL_CSV_REPORT_CONSTANT.DOC_DATE_TO, allFormState, currentReport?.docDateTo, generalCsvReportVM.onHeaderFieldChange])

    const memoIncludeNonVat = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "230px", marginBottom: "20px" }}>
            <NbisCommonField
                fieldKey={"includeNonVat"}
                requiredFieldList={[]}
                fieldLabel={GENERAL_CSV_REPORT_CONSTANT.INCLUDE_NON_VAT}
                fieldType={FieldType.TEXT}
                isReadOnly={true}
                onFieldChange={() => { }} />
            <HPHCheckbox
                label={""}
                checked={currentReport?.includeNonVat === 'Y'}
                disabled={false}
                onChange={(e) => generalCsvReportVM.onCheckboxChange(e.checked, "includeNonVat")}
            />
        </div>
        , [GENERAL_CSV_REPORT_CONSTANT.INCLUDE_NON_VAT, currentReport?.includeNonVat, generalCsvReportVM])


    const memoCustomerType = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "230px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={currentReport?.customerType ?? ''}
                fieldValue={currentReport?.customerType ?? ""}
                fieldLabel={GENERAL_CSV_REPORT_CONSTANT.CUSTOMER_TYPE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'customerType'}
                maxLength={60}
                options={generalCsvReportState.dynamicOptions.customerTypeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    generalCsvReportVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)}
            />
        </div>
        , [currentReport?.customerType, GENERAL_CSV_REPORT_CONSTANT.CUSTOMER_TYPE, generalCsvReportState.dynamicOptions.customerTypeDropdownOptions, generalCsvReportVM])


    return <>
        <Sidebarheader style={{ width: '100%', display: "flex", alignItems: "center" }}>
            <HeaderTitle>{GeneralCsvReportConstant.Title.TITLE}</HeaderTitle>
            {(isLoading) && <Loader Indicator="Spinner" size="Medium" />}
            <StyledAction className="tm-animated-wrapper">
                <HPHButton label={WorkspaceConstant.Common.BUTTON_CLEAR} size={"Small"} theme={"Secondary"} disabled={false} onClick={handleReset} />
                {allowRead && <><div className="add-seperator" />
                    <OverflowMenuButton disabled={false} buttonLabel={WorkspaceConstant.Common.BUTTON_EXPORT_AS} navigationList={memoExportMenu} />
                    <OverflowMenuButton disabled={false} buttonTheme={"Primary"} buttonLabel={WorkspaceConstant.Common.BUTTON_DOWNLOAD_AS} navigationList={memoDownloadMenu} /></>}
            </StyledAction>
        </Sidebarheader>

        <StyledSidebar style={{ width: '100%', height: "100%", border: 'none', backgroundColor: '#FFFFFF', overflow: 'auto', padding: '0' }} position='right'>

            <div className={'add-edit-form'} style={{ height: 'calc(100vh - 125px)', maxHeight: 'calc(100vh - 125px)', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <CriteriaItemContainer>
                            {memoReportType}
                        </CriteriaItemContainer>
                        <CriteriaItemContainer>
                            {memoFileLocation}
                        </CriteriaItemContainer>
                        {currentReport.reportType !== "Output VAT Report" && <>

                            <CriteriaItemContainer>
                                {memoOpsLine}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoMonth}
                                {memoYear}
                            </CriteriaItemContainer>

                        </>}

                        {currentReport.reportType === "Output VAT Report" && <>
                            <CriteriaItemContainer>
                                {memoDocDateFrom}
                                {memoDocDateTo}
                            </CriteriaItemContainer>

                            <CriteriaItemContainer>
                                {memoIncludeNonVat}
                                {memoCustomerType}
                            </CriteriaItemContainer>
                        </>}
                    </div>
                </div>
            </div>
        </StyledSidebar >
    </>
}

export default memo(GeneralCsvReportPanel);
